import React from 'react';
import {useIntl} from 'react-intl';
import {SegmentedControl} from '@forecast-it/design-system';
import Styled from 'styled-components';
import {
	deriveOpportunityStage,
	OPPORTUNITY_STAGE_MESSAGE_IDS,
	winProbabilityBand,
	type OpportunityStageKey,
} from '../../../../opportunities-tab/opportunityStage';

// The five selectable stage bands. Each maps to a canonical win chance (a representative value inside its
// range); 0% (Lost) and 100% (Won) are set through the row's Set-0% / Set-100% quick actions, not this
// control. Each band's displayed % range is taken from winProbabilityBand(value), so the picker always
// matches the list's Win Probability column and the deriveOpportunityStage boundaries.
const BANDS: {stage: OpportunityStageKey; value: number}[] = [
	{stage: 'NEW_LEAD', value: 0.1},
	{stage: 'DISCOVERY', value: 0.2},
	{stage: 'QUALIFIED_OPPORTUNITY', value: 0.4},
	{stage: 'PROPOSAL_SENT', value: 0.6},
	{stage: 'CONTRACT_SENT', value: 0.9},
];

// The DS SegmentedControl item is a fixed 44px single-line control; these two-line band items are taller, so
// we let each item grow (height: auto) and shrink to share the row evenly (min-width: 0 overrides the flex
// item's default min-content width so the five bands fit without overflowing). The wrapper class + element
// selector out-specifies the DS item's own single-class rule.
const BandControl = Styled.div`
	width: 100%;
	& button {
		height: auto;
		min-width: 0;
	}
`;
const ItemBody = Styled.span`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 2px;
	width: 100%;
	text-align: center;
`;
const Range = Styled.span`
	font-weight: 500;
`;
const StageName = Styled.span`
	font-weight: 400;
`;

type Props = {value: number; onChange: (value: number) => void};

// Renders the win-probability picker as stage bands. The band highlighted is the one whose stage the
// current win chance falls in (so a mid-band value like 65% still lights the "Proposal" band), and
// picking a band emits that band's canonical value.
export const WinProbabilityStageBands = ({value, onChange}: Props): JSX.Element => {
	const {formatMessage} = useIntl();
	const selected = BANDS.find(b => b.stage === deriveOpportunityStage(value));
	return (
		<BandControl>
			<SegmentedControl
				value={selected ? String(selected.value) : ''}
				// Radix single ToggleGroup deselects on re-click (emits ''); ignore empty so a value is always held.
				onValueChange={(v: string) => {
					if (v) onChange(Number(v));
				}}
				data-cy="win-probability"
			>
				{BANDS.map(b => {
					const stageLabel = formatMessage({id: OPPORTUNITY_STAGE_MESSAGE_IDS[b.stage]});
					const range = winProbabilityBand(b.value).label;
					return (
						<SegmentedControl.Item
							key={b.stage}
							value={String(b.value)}
							label={`${range} ${stageLabel}`}
							data-cy={`win-band-${b.stage}`}
						>
							<ItemBody>
								<Range>{range}</Range>
								<StageName>{stageLabel}</StageName>
							</ItemBody>
						</SegmentedControl.Item>
					);
				})}
			</SegmentedControl>
		</BandControl>
	);
};

export default WinProbabilityStageBands;
